import { DateTime } from 'luxon';

import { useState } from 'react';

import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import {
  IconCoins,
  IconHistory,
  IconPencil,
  IconPlus,
  IconReceipt2,
  IconReportMoney,
} from '@tabler/icons-react';

import {
  Button,
  CardView,
  Grid,
  Group,
  HistoryListView,
  IconButtonView,
  LabeledValueListView,
  MetricCardView,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  TextInput,
  formatMoney,
} from '@optiro/ui';

import { useResources } from '@/hooks/useResources';
import {
  useCreateTaskCostPrice,
  useCreateTaskExpense,
  useCreateTaskSalesPrice,
  useDeleteTaskExpense,
  useTaskCostPrices,
  useTaskEconomyReport,
  useTaskSalesPrices,
} from '@/hooks/useTasks';
import { Resource, Task, TaskEconomyReport } from '@/types/types';

// ─── Percent formatting ───────────────────────────────────────────────────────

const formatPercent = (value: number, decimalScale = 1) =>
  `${value.toLocaleString('da-DK', { minimumFractionDigits: decimalScale, maximumFractionDigits: decimalScale })}%`;

// ─── Status labels ────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: 'Udkast',
  pending: 'Afventer',
  planning: 'Planlægning',
  active: 'Aktiv',
  completed: 'Afsluttet',
};

// ─── Actuals Ledger ───────────────────────────────────────────────────────────

const ActualsLedger = ({
  taskId,
  report,
  resources,
}: {
  taskId: Task['id'];
  report: TaskEconomyReport;
  resources: Resource[];
}) => {
  const createExpense = useCreateTaskExpense(taskId);
  const deleteExpense = useDeleteTaskExpense(taskId);
  const [opened, { open, close }] = useDisclosure(false);
  const expenseForm = useForm({
    initialValues: {
      resource_id: null as string | null,
      comment: '',
      cost: 0 as number,
    },
  });

  const handleAdd = (values: typeof expenseForm.values) => {
    createExpense.mutate(
      {
        cost: values.cost,
        comment: values.comment,
        resource_id: values.resource_id ? Number(values.resource_id) : null,
      },
      {
        onSuccess: () => {
          expenseForm.reset();
          close();
        },
      }
    );
  };

  const resourceOptions = resources.map((r) => ({ value: String(r.id), label: r.name }));

  return (
    <>
      <CardView
        icon={{ icon: IconCoins, color: 'teal' }}
        title="Omkostninger"
        actions={[{ icon: IconPlus, label: 'Tilføj udgift', onClick: open }]}
      >
        <LabeledValueListView
          dividers
          items={report.expenses.map((expense) => {
            const resource = resources.find((r) => Number(r.id) === expense.resource_id);
            return {
              label: expense.comment?.trim() || resource?.name || 'Udgift',
              money: parseFloat(expense.cost),
              onRemove: () => deleteExpense.mutate(expense.id),
            };
          })}
          total={{ label: 'Total udgifter', money: report.actual_cost }}
        />
      </CardView>

      <Modal opened={opened} onClose={close} title="Tilføj udgift" size="sm" centered>
        <form onSubmit={expenseForm.onSubmit(handleAdd)}>
          <Stack gap="sm">
            <Select
              label="Ressource (valgfri)"
              placeholder="Ingen ressource"
              clearable
              data={resourceOptions}
              {...expenseForm.getInputProps('resource_id')}
            />
            <TextInput
              label="Kommentar"
              placeholder="Fx. ny gaffeltruck, invoice INV-001…"
              {...expenseForm.getInputProps('comment')}
            />
            <NumberInput
              label="Beløb (kr.)"
              placeholder="0"
              min={0}
              {...expenseForm.getInputProps('cost')}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={close}>
                Annuller
              </Button>
              <Button type="submit" loading={createExpense.isPending}>
                Tilføj
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};

// ─── Estimate card (cost/sales price — same value/update/history shape) ───────

type EstimateEntry = {
  id: Task['id'];
  value: string;
  comment?: string | null;
  created_at: string;
  project_status: string;
};

const EstimateCard = ({
  label,
  icon,
  currentValue,
  historyTitle,
  emptyTitle,
  estimates,
  onAdd,
  saving,
  fieldLabel,
  formatValue,
}: {
  label: string;
  icon: React.ReactNode;
  currentValue: number | null;
  historyTitle: string;
  emptyTitle: string;
  estimates: EstimateEntry[];
  onAdd: (value: number, comment?: string) => void;
  saving: boolean;
  fieldLabel: string;
  formatValue: (value: number) => string;
}) => {
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  const addForm = useForm({
    initialValues: { value: currentValue ?? ('' as unknown as number), comment: '' },
  });

  const latest = estimates[0];

  const handleAdd = (values: typeof addForm.values) => {
    onAdd(Number(values.value), values.comment || undefined);
    addForm.reset();
    closeAdd();
  };

  return (
    <>
      <MetricCardView
        label={label}
        icon={icon}
        size="sm"
        value={currentValue !== null ? formatValue(currentValue) : '—'}
        caption={
          latest
            ? `Opdateret ${DateTime.fromISO(latest.created_at).toRelative() ?? ''}`
            : 'Ingen estimat endnu'
        }
        headerAction={
          <IconButtonView icon={IconHistory} label="Se historik" onClick={openHistory} size="sm" />
        }
        footer={
          <Button
            size="xs"
            variant="light"
            mt="xs"
            leftSection={<IconPencil size={13} />}
            onClick={() => {
              addForm.setFieldValue('value', currentValue ?? ('' as unknown as number));
              openAdd();
            }}
          >
            Opdater {label.toLowerCase()}
          </Button>
        }
      />

      <HistoryListView
        opened={historyOpened}
        onClose={closeHistory}
        title={historyTitle}
        emptyTitle={emptyTitle}
        items={estimates.map((e) => ({
          id: e.id,
          value: e.value,
          badgeLabel: STATUS_LABELS[e.project_status] ?? e.project_status,
          comment: e.comment || undefined,
          timestamp: DateTime.fromISO(e.created_at).toFormat("d. LLLL yyyy 'kl.' HH:mm", {
            locale: 'da',
          }),
        }))}
      />

      <Modal
        opened={addOpened}
        onClose={closeAdd}
        title={`Opdater ${label.toLowerCase()}`}
        size="sm"
        centered
      >
        <form onSubmit={addForm.onSubmit(handleAdd)}>
          <Stack gap="sm">
            <NumberInput
              label={fieldLabel}
              placeholder="0"
              min={0}
              thousandSeparator="."
              decimalSeparator=","
              suffix=" kr."
              allowNegative={false}
              {...addForm.getInputProps('value')}
            />
            <TextInput
              label="Kommentar (valgfri)"
              placeholder="Fx. efter omfangsrevision…"
              {...addForm.getInputProps('comment')}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={closeAdd}>
                Annuller
              </Button>
              <Button type="submit" loading={saving}>
                Gem
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};

// ─── EconomyTab ───────────────────────────────────────────────────────────────

type EconomyTabProps = {
  task: Task;
};

export const EconomyTab = ({ task }: EconomyTabProps) => {
  const { data: report } = useTaskEconomyReport(task.id);
  const { data: resourcesAccount } = useResources({ taskId: task.id, resourceableType: 'Account' });
  const { data: resourcesPartner } = useResources({ taskId: task.id, resourceableType: 'Partner' });
  const [includeExpenses, setIncludeExpenses] = useState(false);

  const { data: costEstimates } = useTaskCostPrices(task.id);
  const createCostEstimate = useCreateTaskCostPrice(task.id, task.project_id);
  const { data: salesEstimates } = useTaskSalesPrices(task.id);
  const createSalesEstimate = useCreateTaskSalesPrice(task.id, task.project_id);

  const resources = [...(resourcesAccount ?? []), ...(resourcesPartner ?? [])];

  if (!report) return null;

  const salesPrice = report.estimated_price;
  const costPrice = report.estimated_cost;
  const expenses = report.actual_cost;

  const margin =
    salesPrice !== null && salesPrice > 0
      ? ((salesPrice - (costPrice ?? 0)) / salesPrice) * 100
      : null;

  const marginCalculated =
    salesPrice !== null && salesPrice > 0 && expenses !== null
      ? ((salesPrice - expenses) / salesPrice) * 100
      : null;
  const profitCalculated = salesPrice !== null && expenses !== null ? salesPrice - expenses : null;

  return (
    <Stack gap="md">
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <EstimateCard
            label="Kostpris"
            icon={<IconCoins size={14} />}
            currentValue={report.estimated_cost}
            historyTitle="Kostpris-historik"
            emptyTitle="Ingen kostpriser endnu"
            estimates={(costEstimates ?? []).map((e) => ({
              id: e.id,
              value: formatMoney(parseFloat(e.cost)),
              comment: e.comment,
              created_at: e.created_at,
              project_status: e.project_status,
            }))}
            onAdd={(cost, comment) => createCostEstimate.mutate({ cost, comment })}
            saving={createCostEstimate.isPending}
            fieldLabel="Kostpris (kr.)"
            formatValue={(v) => formatMoney(v)}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <EstimateCard
            label="Salgspris"
            icon={<IconReceipt2 size={14} />}
            currentValue={task.latest_sales_price?.price ?? null}
            historyTitle="Salgspris-historik"
            emptyTitle="Ingen salgspriser endnu"
            estimates={(salesEstimates ?? []).map((e) => ({
              id: e.id,
              value: formatMoney(parseFloat(e.price)),
              comment: e.comment,
              created_at: e.created_at,
              project_status: e.project_status,
            }))}
            onAdd={(price, comment) => createSalesEstimate.mutate({ price, comment })}
            saving={createSalesEstimate.isPending}
            fieldLabel="Salgspris (kr.)"
            formatValue={(v) => formatMoney(v)}
          />
        </Grid.Col>
      </Grid>

      <CardView icon={{ icon: IconReportMoney, color: 'teal' }} title="Nøgletal">
        <LabeledValueListView
          variant="property"
          items={[
            { label: 'Dækningsgrad', value: margin !== null ? formatPercent(margin) : '—' },
            { label: 'Faktisk', money: report.actual_cost },
            ...(includeExpenses
              ? [
                  {
                    label: 'Dækningsgrad (udregnet)',
                    value: marginCalculated !== null ? formatPercent(marginCalculated) : '—',
                  },
                  { label: 'Profit (udregnet)', money: profitCalculated },
                ]
              : []),
          ]}
        />

        <Switch
          mt="md"
          label="Inkluder udgifter (tidsreg. + udgifter) i beregningen"
          checked={includeExpenses}
          onChange={(e) => setIncludeExpenses(e.currentTarget.checked)}
        />
      </CardView>

      <ActualsLedger taskId={task.id} report={report} resources={resources ?? []} />
    </Stack>
  );
};
